import { Injectable, inject } from '@angular/core';
import {
  collection,
  deleteDoc,
  doc,
  DocumentData,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';
import {
  DocumentTemplate,
  RECETA_TEMPLATE_ID,
  TemplateBlock,
  TemplateCustomField,
  TemplateFontSize,
  TemplateListStyle,
  TemplatePageSize,
  TemplateTextAlign,
  TemplateTextStyle,
  defaultTextStyle,
} from '../../models/document-template.type';
import { FIREBASE_FIRESTORE } from '../../firebase/firebase.providers';
import { FirebaseAuthService } from '../auth/firebase-auth.service';
import { MembershipService } from '../auth/membership.service';
import { CallableFunctionsService } from '../functions/callable-functions.service';

@Injectable({ providedIn: 'root' })
export class DocumentTemplateService {
  private firestore = inject(FIREBASE_FIRESTORE);
  private auth = inject(FirebaseAuthService);
  private membership = inject(MembershipService);
  private callables = inject(CallableFunctionsService);

  private get collectionPath(): string {
    const businessId = this.membership.requireSelectedBusinessId();
    return `businesses/${businessId}/documentTemplates`;
  }

  private collectionRef() {
    const [first, ...rest] = this.collectionPath.split('/');
    return collection(this.firestore, first, ...rest);
  }

  private docRef(templateId: string) {
    const [first, ...rest] = this.collectionPath.split('/');
    return doc(this.firestore, first, ...rest, templateId);
  }

  /** Receta first, then most recently edited. Adds the receta for older clinics. */
  async listTemplates(): Promise<DocumentTemplate[]> {
    let templates = await this.fetchTemplates();
    if (!templates.some((template) => template.id === RECETA_TEMPLATE_ID)) {
      try {
        await this.ensureRecetaTemplate();
        templates = await this.fetchTemplates();
      } catch (error) {
        console.error('ensureRecetaTemplate failed', error);
      }
    }
    return [
      ...templates.filter((template) => template.id === RECETA_TEMPLATE_ID),
      ...templates.filter((template) => template.id !== RECETA_TEMPLATE_ID),
    ];
  }

  private async fetchTemplates(): Promise<DocumentTemplate[]> {
    const snap = await getDocs(
      query(this.collectionRef(), orderBy('updatedAt', 'desc'))
    );
    return snap.docs
      .map((templateDoc) => parseTemplate(templateDoc.id, templateDoc.data()))
      .filter((template): template is DocumentTemplate => template != null);
  }

  /** The clinic's receta plantilla, created with the default design if missing. */
  async getRecetaTemplate(): Promise<DocumentTemplate> {
    let template = await this.getTemplateById(RECETA_TEMPLATE_ID);
    if (!template) {
      await this.ensureRecetaTemplate();
      template = await this.getTemplateById(RECETA_TEMPLATE_ID);
    }
    if (!template) {
      throw new Error('No se encontró la plantilla de receta');
    }
    return template;
  }

  /** Creates the receta plantilla if missing; reset restores the default design. */
  async ensureRecetaTemplate(reset = false): Promise<void> {
    await this.callables.ensureRecetaTemplate({
      businessId: this.membership.requireSelectedBusinessId(),
      reset,
    });
  }

  async getTemplateById(templateId: string): Promise<DocumentTemplate | null> {
    const snap = await getDoc(this.docRef(templateId));
    if (!snap.exists()) {
      return null;
    }
    return parseTemplate(snap.id, snap.data());
  }

  async createTemplate(input: {
    name: string;
    pageSize?: TemplatePageSize;
    includeBusinessHeader?: boolean;
    blocks: TemplateBlock[];
    customFields?: TemplateCustomField[];
  }): Promise<string> {
    const uid = this.auth.uniqueId();
    if (!uid) {
      throw new Error('Not signed in');
    }
    const name = input.name.trim();
    if (!name) {
      throw new Error('El nombre de la plantilla es obligatorio');
    }
    if (!input.blocks.length) {
      throw new Error('La plantilla debe tener al menos un bloque');
    }
    if (!this.membership.canCreateTemplate()) {
      throw new Error(
        'Límite de plantillas del plan alcanzado. Mejora tu plan para crear más.'
      );
    }

    const businessId = this.membership.requireSelectedBusinessId();
    const templateId = await this.callables.createDocumentTemplate({
      businessId,
      name,
      pageSize: input.pageSize ?? 'letter',
      includeBusinessHeader: input.includeBusinessHeader !== false,
      blocks: serializeBlocks(input.blocks),
      customFields: serializeCustomFields(input.customFields ?? []),
    });
    this.membership.bumpUsage('templates', 1);
    return templateId;
  }

  async updateTemplate(
    templateId: string,
    input: {
      name: string;
      pageSize?: TemplatePageSize;
      includeBusinessHeader?: boolean;
      blocks: TemplateBlock[];
      customFields?: TemplateCustomField[];
    }
  ): Promise<void> {
    const name = input.name.trim();
    if (!name) {
      throw new Error('El nombre de la plantilla es obligatorio');
    }
    if (!input.blocks.length) {
      throw new Error('La plantilla debe tener al menos un bloque');
    }

    await updateDoc(this.docRef(templateId), {
      name,
      pageSize: input.pageSize ?? 'letter',
      includeBusinessHeader: input.includeBusinessHeader !== false,
      blocks: serializeBlocks(input.blocks),
      customFields: serializeCustomFields(input.customFields ?? []),
      updatedAt: serverTimestamp(),
    });
  }

  async deleteTemplate(templateId: string): Promise<void> {
    if (templateId === RECETA_TEMPLATE_ID) {
      throw new Error('La plantilla de receta no se puede eliminar');
    }
    await deleteDoc(this.docRef(templateId));
    this.membership.bumpUsage('templates', -1);
  }
}

function serializeCustomFields(
  fields: TemplateCustomField[]
): DocumentData[] {
  return fields
    .map((field) => ({
      key: field.key.trim(),
      label: field.label.trim(),
      type: field.type === 'list' ? 'list' : 'text',
    }))
    .filter((field) => field.key && field.label);
}

function serializeBlocks(blocks: TemplateBlock[]): DocumentData[] {
  return blocks.map((block) => {
    switch (block.type) {
      case 'text':
        return {
          id: block.id,
          type: block.type,
          content: block.content,
          style: { ...block.style },
        };
      case 'placeholder':
        return {
          id: block.id,
          type: block.type,
          key: block.key,
          label: block.label,
          style: { ...block.style },
        };
      case 'spacer':
        return {
          id: block.id,
          type: block.type,
          heightMm: block.heightMm,
        };
      case 'divider':
        return {
          id: block.id,
          type: block.type,
        };
      case 'signature':
        return {
          id: block.id,
          type: block.type,
          label: block.label,
        };
      case 'list':
        return {
          id: block.id,
          type: block.type,
          key: block.key,
          label: block.label,
          listStyle: block.listStyle,
        };
    }
  });
}

function parseTemplate(
  id: string,
  data: DocumentData
): DocumentTemplate | null {
  const name = typeof data['name'] === 'string' ? data['name'].trim() : '';
  const createdBy =
    typeof data['createdBy'] === 'string' ? data['createdBy'] : '';
  const rawBlocks = data['blocks'];
  if (!name || !createdBy || !Array.isArray(rawBlocks)) {
    return null;
  }

  const blocks = rawBlocks
    .map((item) => parseBlock(item))
    .filter((block): block is TemplateBlock => block != null);

  if (!blocks.length) {
    return null;
  }

  const pageSize: TemplatePageSize =
    data['pageSize'] === 'letter' ? 'letter' : 'letter';

  return {
    id,
    name,
    ...(data['kind'] === 'receta' ? { kind: 'receta' as const } : {}),
    pageSize,
    includeBusinessHeader: data['includeBusinessHeader'] !== false,
    blocks,
    customFields: parseCustomFields(data['customFields']),
    createdBy,
    createdAt:
      data['createdAt'] instanceof Timestamp ? data['createdAt'] : undefined,
    updatedAt:
      data['updatedAt'] instanceof Timestamp ? data['updatedAt'] : undefined,
  };
}

function parseCustomFields(raw: unknown): TemplateCustomField[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const fields: TemplateCustomField[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') {
      continue;
    }
    const data = item as Record<string, unknown>;
    const key = typeof data['key'] === 'string' ? data['key'].trim() : '';
    const label = typeof data['label'] === 'string' ? data['label'].trim() : '';
    if (!key || !label) {
      continue;
    }
    fields.push({ key, label, type: data['type'] === 'list' ? 'list' : 'text' });
  }
  return fields;
}

function parseBlock(raw: unknown): TemplateBlock | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const data = raw as Record<string, unknown>;
  const id = typeof data['id'] === 'string' ? data['id'] : '';
  const type = data['type'];
  if (!id) {
    return null;
  }

  switch (type) {
    case 'text': {
      const content = typeof data['content'] === 'string' ? data['content'] : '';
      return { id, type, content, style: parseStyle(data['style']) };
    }
    case 'placeholder': {
      const key = typeof data['key'] === 'string' ? data['key'] : '';
      const label = typeof data['label'] === 'string' ? data['label'] : key;
      if (!key) {
        return null;
      }
      return { id, type, key, label, style: parseStyle(data['style']) };
    }
    case 'spacer': {
      const heightMm =
        typeof data['heightMm'] === 'number' && data['heightMm'] > 0
          ? data['heightMm']
          : 8;
      return { id, type, heightMm };
    }
    case 'divider':
      return { id, type };
    case 'signature': {
      const label =
        typeof data['label'] === 'string' && data['label'].trim()
          ? data['label'].trim()
          : 'Firma';
      return { id, type, label };
    }
    case 'list': {
      const key = typeof data['key'] === 'string' ? data['key'] : '';
      const label = typeof data['label'] === 'string' ? data['label'] : key;
      if (!key) {
        return null;
      }
      return { id, type, key, label, listStyle: parseListStyle(data['listStyle']) };
    }
    default:
      return null;
  }
}

function parseListStyle(value: unknown): TemplateListStyle {
  return value === 'numbered' || value === 'bullets' ? value : 'cards';
}

function parseStyle(raw: unknown): TemplateTextStyle {
  const fallback = defaultTextStyle();
  if (!raw || typeof raw !== 'object') {
    return fallback;
  }
  const data = raw as Record<string, unknown>;
  const fontSize = parseFontSize(data['fontSize']) ?? fallback.fontSize;
  const align = parseAlign(data['align']) ?? fallback.align;
  const bold = typeof data['bold'] === 'boolean' ? data['bold'] : fallback.bold;
  return { fontSize, align, bold };
}

function parseFontSize(value: unknown): TemplateFontSize | null {
  return value === 'sm' || value === 'md' || value === 'lg' ? value : null;
}

function parseAlign(value: unknown): TemplateTextAlign | null {
  return value === 'left' || value === 'center' || value === 'right'
    ? value
    : null;
}
